import React from 'react';

const PlanCard = ({ plan, onSelect, isPopular = false }) => {
  // A plan is free if it has no price, price is 0, or has no Stripe price ID
  const hasStripePriceId = plan.stripePriceId && plan.stripePriceId !== null;
  const isFree = !plan.price || plan.price.amount === 0 || !hasStripePriceId;
  const price = plan.price ? Number(plan.price.amount) : 0;
  const currency = plan.price?.currency || 'USD';
  const isRookiePlan = plan.name?.toLowerCase() === 'rookie';

  const formatHours = (hours) => {
    if (hours === null || hours === undefined || Number.isNaN(hours)) {
      return '0';
    }

    const normalized = Math.max(0, hours);
    if (Number.isInteger(normalized)) {
      return `${normalized}`;
    }

    const precision = normalized < 1 ? 2 : 1;
    return `${parseFloat(normalized.toFixed(precision))}`;
  };

  const storageHours =
    plan.storageHours ??
    (plan.storageLimitSeconds !== null && plan.storageLimitSeconds !== undefined
      ? plan.storageLimitSeconds / 3600
      : null);

  const storageFeature = storageHours !== null && storageHours !== undefined
    ? `${formatHours(storageHours)} Hours of Akture Cloud Storage`
    : 'Akture Cloud Storage Included';

  const freePurchaseSeconds =
    plan.freePurchaseSecondsPerMonth ??
    plan.freePurchaseSeconds ??
    0;

  const freeVideosFromPlan =
    plan.freeVideosPerMonth ??
    plan.freeVideos ??
    null;

  const computedFreeVideos =
    freeVideosFromPlan !== null && freeVideosFromPlan !== undefined
      ? freeVideosFromPlan
      : (freePurchaseSeconds > 0 ? Math.max(1, Math.round(freePurchaseSeconds / 3600)) : 0);

  const freeVideoDurationText = (() => {
    if (!freePurchaseSeconds || freePurchaseSeconds <= 0) return null;

    if (freePurchaseSeconds % 3600 === 0) {
      const hours = freePurchaseSeconds / 3600;
      return `${hours === 1 ? '1 hour' : `${hours} hours`} of free video per month`;
    }

    if (freePurchaseSeconds % 60 === 0) {
      const minutes = freePurchaseSeconds / 60;
      return `${minutes} minute${minutes === 1 ? '' : 's'} of free video per month`;
    }

    const hours = freePurchaseSeconds / 3600;
    return `${parseFloat(hours.toFixed(1))} hours of free video per month`;
  })();

  const freeVideoFeature = (() => {
    if (freeVideoDurationText) {
      return freeVideoDurationText;
    }

    if (computedFreeVideos > 0) {
      return `${computedFreeVideos} free video${computedFreeVideos === 1 ? '' : 's'} per month`;
    }

    return null;
  })();

  const featureList = [
    '1080p HD Video',
    'Anytime Video Purchasing',
    'Unlimited Video Downloads',
    storageFeature,
    freeVideoFeature
  ].filter(Boolean);

  return (
    <div className={`relative bg-white rounded-xl p-6 border-2 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 ${
      isPopular 
        ? 'border-brand-red shadow-lg' 
        : 'border-gray-200 hover:border-gray-300'
    }`}>
{isPopular && (
  <div className="absolute top-0 right-0 w-32 h-32 overflow-hidden pointer-events-none">
    <div className="absolute top-5 right-[-45px] w-56 rotate-45 bg-brand-red text-white text-xs font-semibold text-center py-1 shadow-md">
      Popular
    </div>
  </div>
)}


      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-gray-800 mb-2">{plan.name}</h3>
        
        <div className="mb-4">
        {isFree ? (
          <div className="text-3xl font-bold text-gray-800">Free</div>
         ) : (
            <div>
              <span className="text-3xl font-bold text-gray-800">${price.toFixed(2)}</span>
              <span className="text-gray-600">/mo</span>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-3 mb-6">
        {featureList.map((feature, index) => (
          <div key={index} className="flex items-start">
            <div className="flex-shrink-0 w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
              <svg className="w-3 h-3 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
            <span className="text-sm text-gray-700">{feature}</span>
          </div>
        ))}
      </div>

      <button
        onClick={() => onSelect(plan)}
        className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors ${
          isPopular
            ? 'bg-brand-red text-white hover:bg-red-700'
            : 'bg-red-50 text-brand-red hover:bg-red-100'
        }`}
      >
        {isFree ? 'Try It Free' : 'Subscribe'}
      </button>
    </div>
  );
};

export default PlanCard;
